import { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  LinearProgress,
  IconButton,
  Typography,
  Button,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  OpenInNew as OpenIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  VisibilityOff as ViewOffIcon,
} from '@mui/icons-material';
import ProjectTemplateModal from './ProjectTemplateModal';

// Define which demo jobs are actually viewable (same as in WeeklySchedule)
const VIEWABLE_DEMO_JOBS = {
  'J2024-087': '/jobs/pm-demo-job', // Main demo job with full functionality
};

export default function ProjectsDashboard({ projects, onProjectSelect }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Filter projects by job number (primary) or name (secondary)
  const filteredProjects = projects.filter(project => {
    const search = searchTerm.toLowerCase();
    return (
      project.jobNumber.toLowerCase().includes(search) ||
      project.name.toLowerCase().includes(search) ||
      project.client.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      'Planning': 'default',
      'Upcoming': 'info',
      'Active': 'success',
      'Completed': 'default',
    };
    return colors[status] || 'default';
  };

  const calculateHoursPercentage = (actual, budget) => {
    if (!budget) return 0;
    return Math.min(100, (actual / budget) * 100);
  };

  // Calculate summary stats
  const activeProjects = projects.filter(p => p.status === 'Active').length;
  const totalHours = projects.reduce((sum, p) => sum + p.actualHours, 0);
  const totalBudgetHours = projects.reduce((sum, p) => sum + p.budgetHours, 0);
  const overBudgetProjects = projects.filter(p => {
    const percentage = calculateHoursPercentage(p.actualHours, p.budgetHours);
    return percentage > 85;
  }).length;

  return (
    <Box>
      {/* Summary Stats Cards at the Top */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Projects
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {projects.length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Active Projects
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {activeProjects}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Hours Used
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {totalHours.toLocaleString()}
          </Typography>
          {totalBudgetHours > 0 && (
            <Typography variant="caption" color="text.secondary">
              of {totalBudgetHours.toLocaleString()} budgeted
            </Typography>
          )}
        </Paper>
        {overBudgetProjects > 0 && (
          <Paper sx={{ p: 2, flex: 1, bgcolor: 'error.50', borderLeft: '4px solid', borderColor: 'error.main' }}>
            <Typography variant="body2" color="error.dark" gutterBottom>
              Over Budget
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              {overBudgetProjects}
            </Typography>
            <Typography variant="caption" color="error.dark">
              projects need attention
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Header with Search and New Project Button */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          variant="outlined"
          placeholder="Search by Job Number (e.g., J2024-087) or Project Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setTemplateModalOpen(true)}
          sx={{ minWidth: 150 }}
        >
          New Project
        </Button>
      </Box>

      {/* Projects Table */}
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Job Number</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Project Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>PM</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Hours (Used/Budget)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.map((project) => {
              const hoursPercentage = calculateHoursPercentage(project.actualHours, project.budgetHours);
              const isOverBudget = hoursPercentage > 85;

              const isViewable = VIEWABLE_DEMO_JOBS.hasOwnProperty(project.jobNumber);

              return (
                <TableRow
                  key={project.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    opacity: isViewable ? 1 : 0.85,
                  }}
                  onClick={() => {
                    if (VIEWABLE_DEMO_JOBS[project.jobNumber]) {
                      window.location.href = VIEWABLE_DEMO_JOBS[project.jobNumber];
                    } else {
                      setSnackbar({
                        open: true,
                        message: `${project.jobNumber} is a display-only demo project. Click J2024-087 to see the full interactive demo.`
                      });
                    }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {project.jobNumber}
                      </Typography>
                      {isViewable && (
                        <Chip
                          label="INTERACTIVE"
                          size="small"
                          color="primary"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {project.name}
                    </Typography>
                    {project.duration && (
                      <Typography variant="caption" color="text.secondary">
                        ({project.duration})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{project.client}</TableCell>
                  <TableCell>
                    <Chip
                      label={project.pm}
                      size="small"
                      sx={{
                        bgcolor: project.pmColor,
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={project.status}
                      size="small"
                      color={getStatusColor(project.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        color={isOverBudget ? 'error' : 'text.primary'}
                        sx={{ fontWeight: isOverBudget ? 'bold' : 'normal' }}
                      >
                        {project.actualHours}/{project.budgetHours}
                      </Typography>
                      {project.hasLift && (
                        <Chip label="Lift" size="small" color="warning" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={hoursPercentage}
                          color={isOverBudget ? 'error' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="caption">
                        {Math.round(hoursPercentage)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={isViewable ? "Open interactive demo" : "Display-only demo"}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (VIEWABLE_DEMO_JOBS[project.jobNumber]) {
                            window.location.href = VIEWABLE_DEMO_JOBS[project.jobNumber];
                          } else {
                            setSnackbar({
                              open: true,
                              message: `${project.jobNumber} is a display-only demo project. Click J2024-087 to see the full interactive demo.`
                            });
                          }
                        }}
                      >
                        {isViewable ? <ViewIcon fontSize="small" /> : <ViewOffIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>


      {/* Project Template Modal */}
      <ProjectTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
      />

      {/* Snackbar for display-only project notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity="info"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}